/**
 * P125 herd-list filtering, grouping and saved filters (bounded batch D21).
 * Pure functions over the fields the worker already projects (UiAnimal):
 * species, sex, health/ill, enclosure display id, bornTick + current tick.
 *
 * Scope guards (per approved batch):
 * - Age is explicit GAME time: 24 ticks per game day, 365 game days per game
 *   year, computed from bornTick vs current tick. Missing/invalid bornTick
 *   (undefined, non-finite, or after the current tick) yields age null:
 *   such animals are EXCLUDED only while an age filter is active.
 * - "Health" here is the existing sickness flag (ill) - a sickness filter,
 *   not a health-range filter.
 * - Group-by is a reversible view grouping (species/enclosure headers); the
 *   plan's "groups" requirement stays open for a possible authored-groups
 *   reading.
 * - Saved filters are UI preferences in localStorage, never save-game state.
 */

export type HerdSort = 'name' | 'species' | 'health';
export type HerdGroupBy = 'none' | 'species' | 'enclosure';
export type SickFilter = 'all' | 'sick' | 'healthy';
export type SexFilter = 'all' | 'M' | 'F';

export interface HerdFilterState {
  q: string;
  species: string;   // 'all' or species display name
  enclosure: string; // 'all' or enclosure display id
  sex: SexFilter;
  sick: SickFilter;
  ageMinY: number | null; // game years, inclusive
  ageMaxY: number | null; // game years, inclusive
  sort: HerdSort;
  groupBy: HerdGroupBy;
}

export const DEFAULT_HERD_FILTERS: HerdFilterState = {
  q: '', species: 'all', enclosure: 'all', sex: 'all', sick: 'all',
  ageMinY: null, ageMaxY: null, sort: 'name', groupBy: 'none',
};

// Sim calendar source of truth (src/sim/core/clock.ts): 24 ticks per game day,
// 360 game days per game year. Never assume 365.
import { DAY_HOURS, YEAR_DAYS } from '../sim/core/clock';
export const TICKS_PER_GAME_DAY = DAY_HOURS;
export const GAME_DAYS_PER_YEAR = YEAR_DAYS;

export interface FilterableAnimal {
  name: string; species: string; sex: 'M' | 'F'; health: number;
  coat: string; enclosure: string | null; ill: boolean; bornTick?: number;
}

/** Age in whole game days; null when the sim does not say or the value is invalid. */
export function ageGameDays(bornTick: number | undefined, currentTick: number): number | null {
  if (bornTick === undefined || !Number.isFinite(bornTick)) return null;
  if (!Number.isFinite(currentTick) || currentTick < bornTick) return null;
  return Math.floor((currentTick - bornTick) / TICKS_PER_GAME_DAY);
}

/** Compact age label for list rows: "210d" under a year, else "3y". Empty when unknown. */
export function ageLabel(bornTick: number | undefined, currentTick: number): string {
  const d = ageGameDays(bornTick, currentTick);
  if (d === null) return '';
  return d >= GAME_DAYS_PER_YEAR ? `${String(Math.floor(d / GAME_DAYS_PER_YEAR))}y` : `${String(d)}d`;
}

export function applyHerdFilters<T extends FilterableAnimal>(animals: T[], f: HerdFilterState, currentTick: number): T[] {
  const needle = f.q.trim().toLowerCase();
  const ageActive = f.ageMinY !== null || f.ageMaxY !== null;
  const minDays = f.ageMinY !== null ? f.ageMinY * GAME_DAYS_PER_YEAR : null;
  const maxDays = f.ageMaxY !== null ? f.ageMaxY * GAME_DAYS_PER_YEAR : null;
  return animals
    .filter((a) => (f.species === 'all' || a.species === f.species)
      && (f.enclosure === 'all' || a.enclosure === f.enclosure)
      && (f.sex === 'all' || a.sex === f.sex)
      && (f.sick === 'all' || (f.sick === 'sick' ? a.ill : !a.ill))
      && (needle === '' || `${a.name} ${a.species} ${a.coat} ${a.enclosure ?? ''}`.toLowerCase().includes(needle))
      && (!ageActive || (() => {
        const d = ageGameDays(a.bornTick, currentTick);
        return d !== null && (minDays === null || d >= minDays) && (maxDays === null || d <= maxDays);
      })()))
    .sort((x, y) => (f.sort === 'health' ? x.health - y.health
      : f.sort === 'species' ? x.species.localeCompare(y.species) || x.name.localeCompare(y.name)
        : x.name.localeCompare(y.name)));
}

export interface HerdGroup<T> { key: string; label: string; animals: T[] }

/** Reversible view grouping over an already-filtered, already-sorted list. */
export function groupHerdAnimals<T extends FilterableAnimal>(animals: T[], groupBy: HerdGroupBy): HerdGroup<T>[] {
  if (groupBy === 'none') return [{ key: 'all', label: '', animals }];
  const map = new Map<string, T[]>();
  for (const a of animals) {
    const key = groupBy === 'species' ? a.species : (a.enclosure ?? 'No enclosure');
    const list = map.get(key);
    if (list) list.push(a); else map.set(key, [a]);
  }
  return [...map.entries()]
    .sort(([x], [y]) => (x === 'No enclosure' ? 1 : y === 'No enclosure' ? -1 : x.localeCompare(y)))
    .map(([key, list]) => ({ key, label: key, animals: list }));
}

/* ---------- Saved filters: localStorage UI preferences ---------- */

export const SAVED_HERD_FILTERS_KEY = 'ea.herd.savedFilters.v1';

export interface SavedHerdFilter { name: string; state: HerdFilterState }

const SEX: readonly string[] = ['all', 'M', 'F'];
const SICK: readonly string[] = ['all', 'sick', 'healthy'];
const SORT: readonly string[] = ['name', 'species', 'health'];
const GROUP: readonly string[] = ['none', 'species', 'enclosure'];

const pick = <T extends string>(v: unknown, vocab: readonly string[], fallback: T): T =>
  (typeof v === 'string' && vocab.includes(v) ? v : fallback) as T;
const numOrNull = (v: unknown): number | null =>
  (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null);

/** Validate/clamp an untrusted value into a safe HerdFilterState. */
export function sanitizeHerdFilterState(v: unknown): HerdFilterState | null {
  if (typeof v !== 'object' || v === null) return null;
  const r = v as Record<string, unknown>;
  return {
    q: typeof r.q === 'string' ? r.q : '',
    species: typeof r.species === 'string' ? r.species : 'all',
    enclosure: typeof r.enclosure === 'string' ? r.enclosure : 'all',
    sex: pick<SexFilter>(r.sex, SEX, 'all'),
    sick: pick<SickFilter>(r.sick, SICK, 'all'),
    ageMinY: numOrNull(r.ageMinY),
    ageMaxY: numOrNull(r.ageMaxY),
    sort: pick<HerdSort>(r.sort, SORT, 'name'),
    groupBy: pick<HerdGroupBy>(r.groupBy, GROUP, 'none'),
  };
}

/** Load saved filters; corrupt JSON or invalid entries drop out, never throw. */
export function loadSavedHerdFilters(storage: Pick<Storage, 'getItem'>): SavedHerdFilter[] {
  let raw: unknown;
  try { raw = JSON.parse(storage.getItem(SAVED_HERD_FILTERS_KEY) ?? '[]'); } catch { return []; } // unavailable storage or corrupt JSON
  if (!Array.isArray(raw)) return [];
  const out: SavedHerdFilter[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const name = (item as Record<string, unknown>).name;
    const state = sanitizeHerdFilterState((item as Record<string, unknown>).state);
    if (typeof name === 'string' && name.trim() !== '' && state) out.push({ name: name.trim(), state });
  }
  return out.slice(0, 20);
}

export function persistSavedHerdFilters(storage: Pick<Storage, 'setItem'>, list: SavedHerdFilter[]): void {
  try { storage.setItem(SAVED_HERD_FILTERS_KEY, JSON.stringify(list)); } catch { /* quota/unavailable: keep the in-memory list, lose nothing else */ }
}

/** Save (replace same-name), apply-by-name, and delete-by-name helpers. */
export function upsertSavedHerdFilter(list: SavedHerdFilter[], name: string, state: HerdFilterState): SavedHerdFilter[] {
  const trimmed = name.trim();
  if (trimmed === '') return list;
  return [...list.filter((s) => s.name !== trimmed), { name: trimmed, state }];
}

export function deleteSavedHerdFilter(list: SavedHerdFilter[], name: string): SavedHerdFilter[] {
  return list.filter((s) => s.name !== name);
}
